import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import HistoryIcon from "@mui/icons-material/History";
import AddIcon from "@mui/icons-material/Add";
import { SectionTitle } from "../../common/components/SectionTitle";
import { ButtonA } from "../../common/components/ButtonA";
import { DateNavigation } from "./DateNavigation";
import { TaskCounterGrid } from "./TaskCounterGrid";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { useMutation } from "react-query";
import { fetchTaskCounterByDate } from "../miscellaneous/fetchTaskCounterSubmission";
import {
  TaskCounterLocalRow,
  TaskCounterSubmissionRow,
} from "../types/TaskCounterTypes";
import { useUnsavedChangesWarning } from "../hooks/useUnsavedChangesWarning";

export const TaskCounterScreen: FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const initialDate = params.get("date") || dayjs().format("YYYY-MM-DD");
  const focusId = params.get("focusId");

  const [selectedDate, setSelectedDate] = useState(dayjs(initialDate));
  const [rows, setRows] = useState<TaskCounterLocalRow[]>([]);
  const [pendingDate, setPendingDate] = useState<dayjs.Dayjs | null>(null);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const hasDirtyOrSaving = rows.some((r) => r.isDirty || r.isSaving);

  // Route-level navigation guard (works with BrowserRouter)
  const { state: blockerState, proceed, reset } =
    useUnsavedChangesWarning(hasDirtyOrSaving);

  // Browser close/refresh guard
  useEffect(() => {
    if (hasDirtyOrSaving) {
      window.onbeforeunload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
      };
    } else {
      window.onbeforeunload = null;
    }
    return () => { window.onbeforeunload = null; };
  }, [hasDirtyOrSaving]);

  // Load rows for selected date
  const mutationLoadByDate = useMutation(
    (date: string) => fetchTaskCounterByDate(date),
    {
      onSuccess: (res) => {
        if (res.status === 200) {
          const serverRows: TaskCounterSubmissionRow[] = res.data;
          const localRows: TaskCounterLocalRow[] = serverRows.map((r) => ({
            localId: uuidv4(),
            persistedId: r.id,
            taskCounterTemplateId: r.taskCounterTemplateId,
            taskName: r.taskName,
            taskType: r.taskType,
            value: r.value != null ? r.value.toString() : "",
            unitOfMeasure: r.unitOfMeasure,
            comment: r.comment ?? "",
            lastModifiedTime: r.lastModifiedTime,
            saveStatus: "idle",
            saveError: null,
            isDirty: false,
            isSaving: false,
            touched: { taskName: false, value: false },
          }));
          setRows(localRows);
        }
      },
    }
  );

  useEffect(() => {
    mutationLoadByDate.mutate(selectedDate.format("YYYY-MM-DD"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const requestDateChange = useCallback(
    (newDate: dayjs.Dayjs) => {
      if (hasDirtyOrSaving) {
        setPendingDate(newDate);
        setShowLeaveModal(true);
      } else {
        setSelectedDate(newDate);
      }
    },
    [hasDirtyOrSaving]
  );

  const handleLeaveConfirm = () => {
    setShowLeaveModal(false);
    if (pendingDate) {
      setSelectedDate(pendingDate);
      setPendingDate(null);
    }
  };

  const handleLeaveCancel = () => {
    setShowLeaveModal(false);
    setPendingDate(null);
  };

  const handleViewHistory = () => {
    navigate("/task-counter-submission");
  };

  const handleAddNewTask = () => {
    const newRow: TaskCounterLocalRow = {
      localId: uuidv4(),
      persistedId: null,
      taskCounterTemplateId: null,
      taskName: null,
      taskType: null,
      value: "",
      unitOfMeasure: null,
      comment: "",
      lastModifiedTime: null,
      saveStatus: "idle",
      saveError: null,
      isDirty: false,
      isSaving: false,
      touched: { taskName: false, value: false },
    };
    setRows((prev) => [newRow, ...prev]);
  };

  const updateRow = useCallback(
    (localId: string, updates: Partial<TaskCounterLocalRow>) => {
      setRows((prev) =>
        prev.map((r) => (r.localId === localId ? { ...r, ...updates } : r))
      );
    },
    []
  );

  const removeRow = useCallback((localId: string) => {
    setRows((prev) => prev.filter((r) => r.localId !== localId));
  }, []);

  return (
    <div className="mt-9">
      <div className="flex justify-between">
        <SectionTitle name="Task Counter" />
        <div className="flex items-center gap-4">
          <ButtonA
            name="View History"
            icon={<HistoryIcon />}
            onHandleClick={handleViewHistory}
          />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <DateNavigation
          selectedDate={selectedDate}
          onDateChange={requestDateChange}
        />
        <ButtonA
          name="Add New Task"
          icon={<AddIcon />}
          onHandleClick={handleAddNewTask}
        />
      </div>

      <TaskCounterGrid
        rows={rows}
        selectedDate={selectedDate}
        updateRow={updateRow}
        removeRow={removeRow}
        focusId={focusId ? parseInt(focusId) : null}
      />

      {/* Leave modal for date navigation / view history */}
      <UnsavedChangesModal
        isOpen={showLeaveModal}
        onConfirm={handleLeaveConfirm}
        onCancel={handleLeaveCancel}
      />

      {/* Leave modal for route navigation (browser back/forward) */}
      {blockerState === "blocked" ? (
        <UnsavedChangesModal
          isOpen={true}
          onConfirm={() => proceed()}
          onCancel={() => reset()}
        />
      ) : null}
    </div>
  );
};
